import { MessageCircle, CalendarDays, Award, MapPin, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { PROFILE } from "../data/content";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_nivalda-consultorio/artifacts/bplchscf_IMG_2239.png";

export const Hero = () => {
  const waLink = `https://wa.me/${PROFILE.whatsapp_raw}?text=${encodeURIComponent(
    "Olá, Dra. Nivalda. Gostaria de agendar uma consulta."
  )}`;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-grain overflow-hidden border-b-4 border-gold-500"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-royal-800/10 blur-3xl" />
        <div className="absolute top-40 -left-40 w-[420px] h-[420px] rounded-full bg-gold-500/25 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-8 pt-12 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 rounded-full border-2 border-gold-500/50 bg-gold-500/10 px-4 py-1.5 text-[11px] tracking-widest uppercase text-gold-700 font-semibold shadow-sm">
            <Award size={12} /> {PROFILE.crp} • Doutora pela USP
          </div>

          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-royal-900 leading-[1.05]">
            Escuta acolhedora,
            <br />
            <span className="italic text-royal-800">ciência rigorosa</span>,
            <br />
            cuidado humano.
          </h1>

          <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
            Há mais de <strong className="text-royal-800">{PROFILE.experience_years} anos</strong> em consultório,
            a Profª Drª Nivalda P. de Jesus oferece um espaço ético e seguro para psicoterapia,
            neuropsicologia, terapia de casal, sexologia e avaliação psicológica —
            em Santos e por teleconsulta para todo o Brasil.
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-royal-800/8 border border-royal-800/20 text-xs text-royal-800 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-royal-700"></span>
              Presencial em Santos/SP
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold-500/15 border border-gold-500/40 text-xs text-gold-700 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-600"></span>
              Atendimento online em todo o Brasil
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-royal-800/8 border border-royal-800/20 text-xs text-royal-800 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-royal-700"></span>
              Atendimento a imigrantes • PT • EN • ES • FR
            </span>
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <Button
              asChild
              data-testid="hero-whatsapp-button"
              size="lg"
              className="bg-[#25D366] hover:bg-[#1fb457] text-white rounded-md h-12 px-6 text-base"
            >
              <a href={waLink} target="_blank" rel="noreferrer">
                <MessageCircle className="mr-2" size={18} /> Falar no WhatsApp
              </a>
            </Button>
            <Button
              asChild
              data-testid="hero-schedule-button"
              size="lg"
              variant="outline"
              className="border-royal-800 text-royal-800 hover:bg-royal-800 hover:text-white rounded-md h-12 px-6 text-base"
            >
              <a href="#contato">
                <CalendarDays className="mr-2" size={18} /> Agendar consulta
              </a>
            </Button>
            <Button
              asChild
              data-testid="hero-doctoralia-button"
              size="lg"
              variant="outline"
              className="border-gold-500 text-gold-700 hover:bg-gold-500 hover:text-royal-900 rounded-md h-12 px-6 text-base font-medium"
            >
              <a href={PROFILE.doctoralia_booking} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2" size={18} /> Agendar pelo Doctoralia
              </a>
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
            <Stat label="Anos de prática" value="26+" />
            <Stat label="Avaliação média" value="5.0" />
            <Stat label="Depoimentos" value="75+" />
          </div>

          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin size={14} className="text-royal-800" />
            {PROFILE.address_line1}, {PROFILE.city}
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-in-up delay-200">
          <div className="relative">
            <div className="absolute -inset-4 bg-gold-500/30 rounded-[2rem] -rotate-2" />
            <div className="absolute -inset-2 bg-royal-800/15 rounded-[2rem] rotate-1" />
            <img
              src={HERO_IMG}
              alt="Profª Drª Nivalda P. de Jesus"
              data-testid="hero-image"
              className="relative w-full aspect-[4/5] object-cover object-center rounded-[2rem] shadow-xl shadow-royal-900/10"
            />
            <div className="absolute -bottom-5 -left-5 md:-left-8 bg-white rounded-xl shadow-lg border border-border px-5 py-4 max-w-[260px]">
              <div className="flex items-center gap-2 text-gold-600">
                {"★★★★★".split("").map((s, i) => (
                  <span key={i} className="text-base leading-none">{s}</span>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                “Profissional que transmite acolhimento em cada gesto.”
                <br />
                <span className="text-royal-800 font-medium">— Liliane, Doctoralia</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div data-testid={`hero-stat-${label}`}>
    <div className="font-serif-display text-3xl md:text-4xl text-royal-800">{value}</div>
    <div className="text-xs tracking-widest uppercase text-muted-foreground mt-1">
      {label}
    </div>
  </div>
);
